import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { AuthContext } from "../../auth/AuthContext";
import { LoginScreen } from "../../components/protectedRoute";
import { firebaseAuth } from "@/firebase";
import { FASTAPI_SERVER_URL } from "@/constants/aws-constants";
import gptDriverLogo from "../../assets/images/gpt-driver-logo.png";

/**
 * Connect-MCP page.
 *
 * The fastapi OAuth server redirects the user here (instead of rendering
 * its own server-side Firebase login HTML) so we can use this app's
 * branded login UX. Once the user is signed in we exchange their
 * Firebase ID token for an OAuth `login_token` and forward the browser
 * to the consent step (`next` URL returned by the fapi).
 *
 * Query params (set by the fastapi auth-server):
 *   - request_id   (required) — opaque pending-auth-request id
 *   - client_name  (optional) — display name of the MCP client
 */
function ConnectMcpPage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id") ?? "";
  const clientName = searchParams.get("client_name") ?? "MCP client";

  const auth = useContext<any>(AuthContext);
  const { user, loadingUser } = auth;

  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [isExchanging, setIsExchanging] = useState(false);
  const [hasExchanged, setHasExchanged] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    if (loadingUser) return;
    if (user?.uid == null) return;
    if (hasExchanged || isExchanging) return;

    const exchange = async () => {
      setIsExchanging(true);
      setExchangeError(null);
      try {
        // Force-refresh so any custom claims set during sign-in
        // (organisationId in particular) are present on the token.
        const idToken = await firebaseAuth.currentUser!.getIdToken(true);
        const res = await fetch(
          `${FASTAPI_SERVER_URL}/oauth/authorize/login`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              request_id: requestId,
              id_token: idToken,
            }),
          }
        );

        if (!res.ok) {
          const text = await res.text();
          setExchangeError(
            res.status === 400
              ? "This connection request expired. Please retry from your MCP client."
              : `Couldn't complete the connection (${res.status}): ${text}`
          );
          return;
        }

        const body = (await res.json()) as { next: string };
        setHasExchanged(true);
        window.location.href = body.next;
      } catch (e) {
        setExchangeError(
          e instanceof Error ? e.message : "Unexpected error during sign-in."
        );
      } finally {
        setIsExchanging(false);
      }
    };

    void exchange();
  }, [user?.uid, loadingUser, requestId, hasExchanged, isExchanging]);

  if (!requestId) {
    return (
      <CenteredCard>
        <Alert severity="error" sx={{ mb: 2 }}>
          Missing <code>request_id</code>. Open the connect link from your MCP
          client again.
        </Alert>
      </CenteredCard>
    );
  }

  if (loadingUser) {
    return (
      <CenteredCard>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </CenteredCard>
    );
  }

  if (user?.uid == null) {
    return <LoginScreen connectingClientName={clientName} />;
  }

  if (exchangeError) {
    return (
      <CenteredCard>
        <Alert severity="error" sx={{ mb: 2 }}>
          {exchangeError}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => {
            setExchangeError(null);
            setHasExchanged(false);
          }}
        >
          Try again
        </Button>
      </CenteredCard>
    );
  }

  return (
    <CenteredCard>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Connecting <strong>{clientName}</strong> to your GPT Driver workspace...
      </Typography>
    </CenteredCard>
  );
}

const CenteredCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      height: "100%",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      bgcolor: "white",
      p: 4,
    }}
  >
    <Box
      component="img"
      src={gptDriverLogo}
      alt="GPT Driver"
      sx={{ width: 96, mb: 3 }}
    />
    {children}
  </Box>
);

export default ConnectMcpPage;
